import styled from "@emotion/styled";
import { FC, useMemo } from "react";
import { useLocation } from "react-router";

import { ChevronLeft, ChevronRight } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, RadiusToken, SpacingToken, Text } from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { getPagination } from "helpers/pagination";
import { getRouteByPathname } from "helpers/tickets";
import { withVar } from "themes/helpers";

interface IProps {
  currentPage: number;
  totalPages: number;
  totalPagesLimit?: number;
  onPageChange: (page: number) => void;
}

export const Pagination: FC<IProps> = props => {
  const location = useLocation();

  const paginationOptions = useMemo(
    () => ({
      arrowLeft: (page: number): JSX.Element => (
        <Button kind="plain" size="xcompact" disabled={page === 1} icon={<Icon source={ChevronLeft} />} />
      ),
      arrowRight: (page: number, totalPages: number): JSX.Element => (
        <Button kind="plain" disabled={page === totalPages} size="xcompact" icon={<Icon source={ChevronRight} />} />
      ),
      page: (pageNumber: number): JSX.Element => (
        <PageButton size="sm" disabled={false} active={false}>
          {pageNumber}
        </PageButton>
      ),
      currentPage: (pageNumber: number): JSX.Element => (
        <PageButton size="sm" active disabled>
          {pageNumber}
        </PageButton>
      ),
      onClick: (page: number, _e: React.MouseEvent, context?: string): void => {
        const route = getRouteByPathname(location.pathname);
        if (route !== null && context) {
          trackEngagement("Tickets: Pagination used", EventPlace.Tickets, { source: context, route });
        }
        props.onPageChange(page);
      },
      totalPagesLimit: props.totalPagesLimit,
    }),
    [location.pathname, props]
  );

  const { currentPage, totalPages } = props;
  if (totalPages <= 1) {
    return null;
  }

  return (
    <Wrapper>
      {getPagination(currentPage, totalPages, paginationOptions).map((el, index) => (
        <span key={index}>{el}</span>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(${SpacingToken.Spacing05});
`;

const PageButton = styled(Text)<{ disabled: boolean; active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: var(${SpacingToken.Spacing6});
  min-height: var(${SpacingToken.Spacing6});
  background-color: ${({ active }): string => (active ? withVar(DesignToken.ActionPrimaryDefault) : "transparent")};
  border-radius: var(${RadiusToken.Radius2});
  color: ${({ active }): string =>
    withVar(active ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)};
  padding: 0 var(${SpacingToken.Spacing2});

  &:hover {
    background-color: ${({ active }): string =>
      withVar(active ? DesignToken.ActionPrimaryDefault : DesignToken.SurfaceSecondaryHover)};
    cursor: ${({ disabled }): string => (disabled ? "not-allowed" : "pointer")};
  }
`;
